import { FormEvent, useCallback, useEffect, useState, type CSSProperties } from "react";
import { PanelLoading } from "../../components/Loading";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { lotsApi, type LotTraceability, type LotUsageTargetKind } from "../../api/lots";
import { deliveriesApi, type Delivery } from "../../api/deliveries";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { ComplaintStatusBadge } from "../../components/ComplaintStatusBadge";
import { localizedName } from "../../lib/localizedName";
import Ic from "../../components/icons.jsx";

const TARGET_KINDS: LotUsageTargetKind[] = ["ORDER", "COMPLAINT", "PRODUCTION", "OTHER"];

/**
 * One lot's whole story: where it came from, what it averages, where it went.
 *
 * The average is **weighted by delivered weight**, not by delivery count — ten
 * tonnes at 40% and thirty at 44% is 43%, and that is the figure a customer
 * eventually asks about.
 */
export default function LotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [data, setData] = useState<LotTraceability | null>(null);
  const [assignable, setAssignable] = useState<Delivery[]>([]);
  const [pickedDelivery, setPickedDelivery] = useState("");
  const [usageOpen, setUsageOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = hasPermission("lots:manage");
  const locale = i18n.resolvedLanguage ?? "sr";

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setData(await lotsApi.get(id));
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only weighed deliveries can enter a lot — an announced truck has no net
  // weight yet and would poison both the total and the average.
  useEffect(() => {
    if (!canManage) return;
    // Birač isporuke koja se dodaje u šaržu. Strana je velika jer se posle čitanja zadržavaju samo
    // one koje još nisu ni u jednoj šarži, pa mali broj ume da se isprazni do nule.
    Promise.all([
      deliveriesApi.list({ status: "RECEIVED" }, 0, 200),
      deliveriesApi.list({ status: "SETTLED" }, 0, 200),
    ])
      .then(([a, b]) => setAssignable([...a.content, ...b.content].filter((d) => d.receivingLotId == null)))
      .catch(() => setAssignable([]));
  }, [canManage, data]);

  async function assign(e: FormEvent) {
    e.preventDefault();
    if (!id || !pickedDelivery) return;
    try {
      await lotsApi.assignDelivery(id, pickedDelivery);
      setPickedDelivery("");
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function remove(deliveryId: string) {
    if (!id) return;
    try {
      await lotsApi.removeDelivery(id, deliveryId);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;
  if (!data) return <div className="page"><PanelLoading text={t("common.loading")} /></div>;

  const fmtDate = (v: string) => new Date(v).toLocaleDateString(locale);

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <nav style={{ display: "flex", gap: 6, fontSize: 12 }}>
        <Link to="/procurement/lots" style={{ color: "var(--ink-3)", textDecoration: "none" }}>
          {t("lots.title", "Intake lots")}
        </Link>
        <span style={{ color: "var(--ink-4)" }}>/</span>
        <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{data.lot.lotNumber}</span>
      </nav>

      <header style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>{data.lot.lotNumber}</h1>
        <span style={{ color: "var(--ink-3)" }}>
          {fmtDate(data.lot.receivedOn)} · {data.lot.storageLocation ?? "—"}
        </span>
      </header>

      <section style={card}>
        <h2 style={cardTitle}>{t("lots.from", "Where it came from")}</h2>
        {data.deliveries.length === 0 ? (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("lots.noDeliveries", "No deliveries in this lot yet.")}
          </p>
        ) : (
          <table className="tbl" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>{t("deliveries.list.col.company", "Supplier")}</th>
                <th>{t("deliveries.list.col.announcedDate", "Announced")}</th>
                <th>{t("deliveries.list.col.netKg", "Net (kg)")}</th>
                <th>{t("deliveries.transport.ticketNo", "Weighbridge ticket")}</th>
                {canManage && <th style={{ textAlign: "right" }}>{t("common.actions", "Actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {data.deliveries.map((d) => (
                <tr key={d.deliveryId}>
                  <td>{d.companyName}</td>
                  <td>{fmtDate(d.announcedDate)}</td>
                  <td>{d.netKg ?? "—"}</td>
                  <td><code>{d.weighbridgeTicketNo ?? "—"}</code></td>
                  {canManage && (
                    <td style={{ textAlign: "right" }}>
                      <button type="button" className="btn sm" disabled={!hasPermission("section:intake_lots:full")} onClick={() => void remove(d.deliveryId)}>
                        {t("lots.removeDelivery", "Remove")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && assignable.length > 0 && (
          <form onSubmit={assign} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 260 }}>
              <span style={fieldLabel}>{t("lots.addDelivery", "Add a weighed delivery")}</span>
              <Select<string>
                value={pickedDelivery}
                onChange={setPickedDelivery}
                ariaLabel={t("lots.addDelivery", "Add a weighed delivery")}
                options={[
                  { value: "", label: t("lots.pickDelivery", "Pick a delivery") },
                  ...assignable.map((d) => ({
                    value: d.id,
                    // Uvezen prijem nema najavu, pa u biraču stoji dan prijema.
                    label: `${d.companyName} · ${fmtDate(d.receivedOn ?? d.announcedDate ?? "")} · ${d.netKg ?? "?"} kg`,
                  })),
                ]}
              />
            </label>
            <button type="submit" className="btn" disabled={!pickedDelivery}>
              <Ic.plus /> {t("lots.addDelivery", "Add a weighed delivery")}
            </button>
          </form>
        )}
      </section>

      <section style={card}>
        <h2 style={cardTitle}>{t("lots.averageQuality", "Average quality")}</h2>
        <p style={{ margin: 0, fontSize: 11, color: "var(--ink-3)" }}>
          {t("lots.weightedHint", "Weighted by delivered weight: 10 t at 40% and 30 t at 44% averages 43%, not 42%.")}
        </p>
        {data.averageQuality.length === 0 ? (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("lots.noQuality", "Nothing measured in this lot yet.")}
          </p>
        ) : (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {data.averageQuality.map((q) => (
              <div key={q.parameterId} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={fieldLabel}>
                  {localizedName({ name: q.parameterKey, nameI18n: q.nameI18n }, locale)} ({q.unit})
                </span>
                <span style={{ fontSize: 15, color: "var(--ink)", fontWeight: 600 }}>
                  {/* An average is a figure by definition: a yes/no finding has none,
                      and the backend leaves those out of the average entirely. */}
                  {q.value == null ? "—" : q.value.toFixed(q.decimals)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <h2 style={cardTitle}>{t("lots.usage", "Where it went")}</h2>
          {canManage && (
            <button type="button" className="btn sm" onClick={() => setUsageOpen(true)}>
              <Ic.plus /> {t("lots.recordUsage", "Record usage")}
            </button>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
          {t("lots.balance", "Total {{total}} kg · used {{used}} kg · remaining {{remaining}} kg", {
            total: data.totalNetKg,
            used: data.usedKg,
            remaining: data.remainingKg,
          })}
        </p>
        {data.usage.length === 0 ? (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("lots.noUsage", "Nothing recorded yet.")}
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {data.usage.map((u) => (
              <li key={u.id} style={row}>
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 13 }}>
                    {t(`lots.targetKind.${u.targetKind}`, u.targetKind)} · {u.quantityKg} kg
                  </span>
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {fmtDate(u.usedOn)}
                    {u.externalReference ? ` · ${u.externalReference}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {usageOpen && id && (
          <UsageForm
            lotId={id}
            onClose={() => setUsageOpen(false)}
            onSaved={async () => {
              setUsageOpen(false);
              await load();
            }}
          />
        )}
      </section>

      {data.complaints.length > 0 && (
        <section style={card}>
          <h2 style={cardTitle}>{t("lots.complaints", "Complaints about this lot")}</h2>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {data.complaints.map((c) => (
              <li key={c.id} style={row}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/complaints/${c.id}`} style={{ display: "block", fontSize: 13, color: "var(--brand-primary)" }}>
                    {c.caseNumber} · {c.title}
                  </Link>
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {c.companyName} · {fmtDate(c.createdAt)}
                  </span>
                </span>
                <ComplaintStatusBadge status={c.status} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function UsageForm({
  lotId,
  onClose,
  onSaved,
}: {
  lotId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [usedOn, setUsedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantityKg, setQuantityKg] = useState("");
  const [targetKind, setTargetKind] = useState<LotUsageTargetKind>("PRODUCTION");
  const [externalReference, setExternalReference] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await lotsApi.recordUsage(lotId, {
        usedOn,
        quantityKg: Number(quantityKg),
        targetKind,
        externalReference: externalReference.trim() || null,
      });
      toast.success(t("lots.usageSaved", "Usage recorded."));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 8 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={fieldLabel}>{t("lots.usedOn", "Used on")}</span>
        <input type="date" className="input" value={usedOn} onChange={(e) => setUsedOn(e.target.value)} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={fieldLabel}>{t("lots.quantity", "Quantity (kg)")}</span>
        <input type="number" min="0" step="0.01" className="input" value={quantityKg} onChange={(e) => setQuantityKg(e.target.value)} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
        <span style={fieldLabel}>{t("lots.targetKind", "Destination")}</span>
        <Select<LotUsageTargetKind>
          value={targetKind}
          onChange={setTargetKind}
          ariaLabel={t("lots.targetKind", "Destination")}
          options={TARGET_KINDS.map((k) => ({ value: k, label: t(`lots.targetKind.${k}`, k) }))}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 200 }}>
        <span style={fieldLabel}>{t("lots.externalReference", "ERP reference")}</span>
        <input className="input" value={externalReference} onChange={(e) => setExternalReference(e.target.value)} />
      </label>
      <button type="submit" className="btn primary" disabled={busy || !quantityKg}>
        {t("common.save", "Save")}
      </button>
      <button type="button" className="btn" onClick={onClose}>{t("common.cancel", "Cancel")}</button>
    </form>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
};
